import { Trans } from '@lingui/macro'
import { Autocomplete, Box, Button, Grid, Icon, TextField } from '@mui/material'
import { getFormPages } from 'app/services/sfAuth/sfData/sfForms'
import { FormTopLabel } from 'app/views/common-components/forms/FormTopLabel'
import { TextFieldWithFormik } from 'app/views/common-components/with-formik/TextFieldWithFormik'
import { PortalTabHeader } from 'app/views/common/PortalTabHeader'
import { constructFormAddressString } from 'app/views/forms/common/Common'
import Loading from 'egret/components/EgretLoadable/Loading'
import { Formik } from 'formik'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'
import Form from '../../forms/Form'

const GenerateFillablePDFForm = () => {
  const [loading, setLoading] = useState(true)
  const [avaliableForms, setAvaliableForms] = useState([])
  const [validationSchema, setValidationSchema] = useState({})
  const [generatePDF, setGeneratePdf] = useState(false)
  const [generatedPreviews, setGeneratedPreviews] = useState([])
  const [configurationId, setConfigurationId] = useState(null)
  const user = useSelector((state) => state.user)
  const configuration = useSelector((state) => state.configuration)
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const fetchData = () => {
    Promise.all([getFormPages()]).then(([formPages, configObjects]) => {
      setAvaliableForms(formPages)
      setLoading(false)
    })
  }

  useEffect(() => {
    fetchData()
    setGeneratedPreviews(
      configuration.CONFIGURED_PRINT_PREVIEWS
        ? configuration.CONFIGURED_PRINT_PREVIEWS
        : []
    )
  }, [])

  const buildValidationSchema = (objects) => {
    const toSet = {
      name: Yup.string()
    }
    objects.forEach((obj) => {
      toSet[obj.identId] = Yup.string().nullable()
    })
    setValidationSchema(toSet)
  }

  if (loading) {
    return <Loading atCenter />
  }

  const formIdToConfig = {}
  avaliableForms.forEach((form) => {
    formIdToConfig[form.id] = form.config
  })

  return (
    <div className='portal-tab-container'>
      <PortalTabHeader
        header={<Trans>TAB_LABEL_GENERATE_PDF_FROM_FORM</Trans>}
      />
      <Formik
        validationSchema={Yup.object().shape(validationSchema)}
        initialValues={{
          form: ''
        }}
        validateOnBlur
      >
        {({
          values,
          setFieldValue,
          setValues,
          isValid,
          validateForm,
          resetForm,
          dirty
        }) => {
          const { form, name, domain } = values

          return (
            <div>
              <FormTopLabel>
                <Trans>Form</Trans>
              </FormTopLabel>
              <Autocomplete
                fullWidth
                options={avaliableForms
                  .filter(form => form.config.supportedFormType && form.config.supportedFormType.includes('fillable-pdf'))
                  .sort((a, b) => {
                    const titleA = a.name[user.language] || ''
                    const titleB = b.name[user.language] || ''
                    return titleA.localeCompare(titleB)
                  })}
                value={avaliableForms.find((f) => f.id === values.form) || null}
                getOptionLabel={(option) => {
                  const title = option.name?.[user.language] || ''
                  const comments = option.comments
                    ? ` [${option.comments}]`
                    : ''
                  const version = option.config?.version
                    ? ` [ v.${option.config.version} ]`
                    : ''
                  return title + comments + version
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, selectedForm) => {
                  const selectedId = selectedForm?.id || ''
                  setGeneratePdf(false)
                  const objects = formIdToConfig[selectedId]?.objectsConnected
                  if (objects) {
                    buildValidationSchema(objects)
                  }
                  const emptyObjectValues = {}
                  formIdToConfig[selectedId]?.objectsConnected?.forEach(
                    (obj) => {
                      emptyObjectValues[obj.identId] = ''
                    }
                  )
                  resetForm({
                    values: {
                      form: selectedId,
                      name: '',
                      ...emptyObjectValues
                    }
                  })
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant='outlined'
                  />
                )}
              />

              {formIdToConfig[form] &&
                formIdToConfig[form].objectsConnected && (
                  <div style={{ paddingTop: 15 }}>
                    <Box sx={{ paddingBottom: '16px' }}>
                      <Trans>Objects connected</Trans>
                    </Box>

                    {formIdToConfig[form].objectsConnected.map(
                      (object, index) => {
                        return (
                          <Grid container key={index} direction='column'>
                            <TextFieldWithFormik
                              style={{ marginTop: 10 }}
                              topLabel={object.name + ' [' + object.type + ']'}
                              name={object.identId}
                              fullWidth
                              variant='outlined'
                              onChange={(e) => {
                                setFieldValue(object.identId, e.target.value)
                                setGeneratePdf(false)
                              }}
                            />
                          </Grid>
                        )
                      }
                    )}

                    <Button
                      variant='contained'
                      color='primary'
                      disabled={!values.form}
                      fullWidth
                      style={{ marginTop: 24, marginBottom: 16 }}
                      onClick={(e) => {
                        setGeneratePdf(true)
                      }}
                    >
                      <Trans>GENERATE_PDF_FROM_FORM</Trans>
                      <Icon style={{ marginLeft: 5 }}>picture_as_pdf</Icon>
                    </Button>
                  </div>
              )}

              {generatePDF && (
                <div style={{ height: 850 }}>
                  <Form
                    fixedDisplay
                    defaultFormType='fillable-pdf'
                    fetchString={
                      constructFormAddressString({
                        objectsConnected:
                              formIdToConfig[form].objectsConnected.filter(obj => values[obj.identId]),
                        ids: Object.fromEntries(
                          formIdToConfig[form].objectsConnected
                            .filter(obj => values[obj.identId])
                            .map(
                              (obj) => [obj.type, values[obj.identId]]
                            )
                        )
                      })
                    }
                    formId={values.form}
                  />
                </div>
              )}
            </div>
          )
        }}
      </Formik>
    </div>
  )
}

export default GenerateFillablePDFForm
